<script>
import { slug } from '../../../../../Utils/StringUtils.ts'
import { globalMethodsMixin, globalVariablesMixin } from '../../../Mixins/global'
import mixinComponent from '../../../Mixins/mixinComponent'

export default {
  mixins: [globalVariablesMixin, globalMethodsMixin, mixinComponent],
  props: {
    id: { type: String, required: true },
    columns: { type: Array, required: true },
    order: { type: Array, required: false, default: () => [] },
    fetch: {
      type: Function,
      required: false,
      default: () => (callback) => callback({ data: [], recordsTotal: 0, recordsFiltered: 0 }),
    },
    zeroRecords: { type: String, default: null },
    firstLoad: { type: Boolean, default: true },
    pageLength: { type: Number, default: 25 },
    items: {
      type: Array,
      required: false,
      default: null,
    },
    paginate: {
      type: Boolean,
      required: false,
      default: true,
    },
    paginationMode: {
      type: String,
      required: false,
      default: 'client',
    },
    options: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['lookup', 'row-click'],
  data: () => ({
    dataTableInstance: null,
  }),
  computed: {
    datatableColumnsDefinition() {
      const self = this
      return this.columns
        .map(({ name, type, displayable = true, position = 0, title, formatter, style, options }) => {
          let render
          switch (formatter) {
            case 'currency':
              render = (data) => {
                if (typeof data === 'number' || !isNaN(Number(data))) {
                  data = Number(data)
                  return this.$root.$n(Number(data), 'currency')
                } else {
                  return data
                }
              }
              break
            case 'date':
              render = (data, type, row) => {
                if (type === 'display') {
                  return '<span data-sort="' + row[name] + '">' + this.setDate(data, 'date') + '</span>'
                }
                return data
              }
              break
            case 'boolean':
              render = (data) => {
                return data ? this.$root.$t('main.bool.' + data) : '-'
              }
              break
            case 'entity':
              render = (data, type, row) => {
                return data ? this.renderEntity(data, options.renderId, row) : '-'
              }
              break
            case 'user':
              render = (data) => {
                return data ? this.renderUser(data) : '-'
              }
              break
            case 'context':
              render = (data, type, row) => {
                return data ? this.renderContext(data, options.renderType, row[options.renderId], row) : '-'
              }
              break
            case 'status':
              render = (data) => {
                return data ? this.$root.$t(`${options.renderType}.status.${data}.label`) : '-'
              }
              break
            case 'action':
              render = (data, type, row) => (data != null ? this.renderAction(data, options.renderType, row) : '-')
              break
            case 'custom':
              render = (data, type, row) => (type === 'display' ? options.renderer.bind(self)(data, type, row) : data)
              break
            default:
              render = (data) => {
                return data ? this.textRenderer(data) : '-'
              }
          }

          let className, width
          switch (true) {
            case style != undefined && style.className != undefined:
              className = style.className
            case style != undefined && style.width != undefined:
              width = style.width
              break
          }
          return {
            name,
            data: name,
            type,
            orderable: true,
            visible: displayable ?? false,
            position,
            searchable: true,
            title,
            className,
            width,
            render,
          }
        })
        .sort((a, b) => a.position - b.position)
    },
    languageInfo() {
      let defaultLanguage = {}

      const languageKeys = [
        'lengthMenu',
        'processing',
        'search',
        'info',
        'infoEmpty',
        'infoFiltered',
        'infoPostFix',
        'loadingRecords',
        'zeroRecords',
        'emptyTable',
        'paginate.first',
        'paginate.previous',
        'paginate.next',
        'paginate.last',
        'aria.sortAscending',
        'aria.sortDescending',
      ]

      for (const key of languageKeys) {
        const message = this.$t(`dataTable.${key}`)
        if (key.indexOf('.') > 0) {
          const keySplit = key.split('.')
          if (!defaultLanguage[keySplit[0]]) {
            defaultLanguage[keySplit[0]] = {}
          }
          defaultLanguage[keySplit[0]][keySplit[1]] = message
        } else {
          defaultLanguage[key] = message
        }
      }

      if (this.zeroRecords) {
        defaultLanguage['zeroRecords'] = this.zeroRecords
        defaultLanguage['emptyTable'] = this.zeroRecords
      }

      return defaultLanguage
    },
    hasAccessToEntity() {
      return this.hasRole('ROLE_APP_ADMIN_ENTITY_FULL') && (user.isBusinessMan || this.hasRole('ROLE_ACCESS_ENTITY'))
    },
  },
  watch: {
    datatableColumnsDefinition: {
      deep: true,
      handler() {
        this.renderDataTable()
      },
    },
  },
  mounted() {
    if (this.firstLoad) {
      this.renderDataTable()
    }
  },
  methods: {
    renderDataTable() {
      if (this.dataTableInstance) {
        this.dataTableInstance.destroy()
      }

      const options = Object.assign({}, this.options, {
        columns: this.datatableColumnsDefinition,
        language: this.languageInfo,
        order: this.order,
        info: false,
        filter: true,
        pageLength: this.pageLength,
        search: {
          smart: false,
        },
        searchDelay: 1000,
        paging: this.paginate,
        layout: {
          bottomEnd: {
            paging: {
              firstLast: false,
            },
          },
        },
      })

      if (this.paginate && this.paginationMode === 'server') {
        options['serverSide'] = true
      }

      if (this.items === null) {
        options['ajax'] = async (data, callback, settings) => {
          let filterInput = document.querySelector(`#${this.id}_wrapper input[aria-controls="${this.id}"]`)
          if (filterInput) {
            filterInput.disabled = true
          }

          const sort = data.order ?? []

          await this.fetch(callback, {
            page: 1 + data.start / data.length,
            search: data.search?.value,
            limit: data.length,
            sort: sort.map(({ dir: order, name: field }) => ({ order, field })),
          })
          if (filterInput) {
            filterInput.disabled = false
          }
        }
      } else {
        options['data'] = this.items
      }

      if (options.rowClickable) {
        options['createdRow'] = (row) => {
          row.classList.add('cursor-pointer')
        }
      }

      this.dataTableInstance = $(`#${this.id}`).DataTable(options)
      this.setupLookupListener()
    },
    setupLookupListener() {
      $(`#${this.id}`).on('click', 'i[data-action="lookup"]', (e) => {
        e.stopPropagation()
        this.$emit('lookup', e.currentTarget.id)
      })
      $(`#${this.id}`).on('click', 'tr', (e) => {
        const row = this.dataTableInstance.row(e.currentTarget)
        this.$emit('row-click', row.data())
      })
    },
    textRenderer: $.fn.dataTable.render.text().display,
    commentButton(data) {
      if (data.length < 50) {
        return data
      }
      const strippedData = data.replace(/<[^>]*>/g, '')

      const escapedData = strippedData.replace(/"/g, '&quot;')

      return `<i class="fa fa-comment" title="${escapedData}'"></i>`
    },
    renderEntity(data, renderId, options) {
      let id
      if (renderId.includes('.')) {
        id = renderId.split('.').reduce((acc, part) => acc && acc[part], options)
      } else {
        id = options[renderId]
      }

      if (id === 'undefined') {
        id = options['client']['id']
      }

      if (id !== 'undefined') {
        if (this.hasAccessToEntity) {
          return `<a href="/#/societes/${id}-${slug(data)}" target="_blank">${data}</a>`
        } else {
          return this.textRenderer(data)
        }
      } else {
        return this.textRenderer(data)
      }
    },
    renderUser(data) {
      return data?.name ?? `${data.firstname} ${data.lastname}`
    },
    renderContext(data, renderType, renderId, row) {
      switch (renderType) {
        case 'simulation':
          let id = renderId ?? data
          return (
            "<a href='../devis/simulation.php?i=" +
            id +
            "&p=devis&index=simulation' target='_blank'>" +
            this.textRenderer(data) +
            '</a>'
          )
        case 'contracts':
          return (
            "<a href='../contrats/contract.php?i=" + renderId + "' target='_blank'>" + this.textRenderer(data) + '</a>'
          )
        case 'invoice':
          return this.textRenderer(data)
        case 'supplierInvoice':
          return '<a href="#" class="invoice-detail" data-id="' + row.id + '">' + this.textRenderer(data) + '</a>'
        case 'portefeuille':
          return (
            "<a href='../portefeuille/portefeuille.php?i=" +
            renderId +
            "&p=detail' target='_blank'>" +
            this.textRenderer(data) +
            '</a>'
          )
        default:
          return this.textRenderer(data)
      }
    },
    renderAction(data, renderType, options) {
      switch (renderType) {
        case 'lookup':
          return (
            '<i class="fas fa-search" style="cursor:pointer;" data-action="lookup" id="' +
            (options?.actionId ?? data) +
            '"></i>'
          )
        case 'download':
          return '<i class="fas fa-download" style="cursor:pointer;" id="' + data + '"></i>'
        case 'delete':
          if (['CLIENT_CONTRACT', 'CANCELED'].includes(options['statusLabel'])) {
            return '-'
          }

          return '<i class="fas fa-times " style="cursor:pointer;" data-action="delete" id="' + data + '"></i>'
        case 'comment':
          return this.commentButton(data)
      }
    },
  },
}
</script>
<template>
  <div class="table-responsive-sm" style="overflow-x: auto">
    <table :id="id" class="table table-striped table-hover w-100">
      <slot></slot>
      <tfoot>
        <slot name="footer"></slot>
      </tfoot>
    </table>
  </div>
</template>
